import TransformationCapabilitiesDto from '../../classes/dtos/transformationmodule/TransformationCapabilitiesDto';

// The AI sometimes names the wiring "FieldIds" (older Concatenate description) instead of "InputFieldIds" (and a single string "FieldId" for one-input steps).
const INPUT_KEYS = ['InputFieldIds', 'FieldIds', 'FieldId'];

export interface TransformationStep {
    Index: number;
    LogicId: number | null;
    Label: string;
    // Settings of the step without the wiring (InputFieldIds), as short "key: value" text.
    Details: string;
    // Ids consumed by the step: source field ids, or the ResultId of an earlier step.
    Inputs: string[];
    ResultId: string;
}

// The server returns the settings serialized as PascalCase, but the LLM may drift: read keys case-insensitively.
export const pick = (obj: any, key: string): any => {
    if (obj === null || typeof obj !== 'object') {
        return undefined;
    }
    const found = Object.keys(obj).find((k) => k.toLowerCase() === key.toLowerCase());
    return found === undefined ? undefined : obj[found];
};

const parseInnerParameters = (raw: any): any => {
    if (typeof raw === 'string') {
        try { return JSON.parse(raw); } catch { return undefined; }
    }
    return raw;
};

const formatDetails = (inner: any): string => {
    if (inner === null || typeof inner !== 'object') {
        return "";
    }
    const keys = Object.keys(inner).filter((k) => !INPUT_KEYS.some((i) => i.toLowerCase() === k.toLowerCase()));
    // A constant transformation only carries its Value: show it bare.
    if (keys.length === 1 && keys[0].toLowerCase() === 'value') {
        return String(inner[keys[0]]);
    }
    return keys
        .map((k) => `${k}: ${typeof inner[k] === 'object' ? JSON.stringify(inner[k]) : String(inner[k])}`)
        .join(', ');
};

// Labels of the transformations used by the steps, keyed by logic id, stored next to the steps (extra "Labels"
// property, ignored by the back-end deserializer).
export function withLabels(parameters: string, labels: Record<string, string>): string {
    try {
        return JSON.stringify({ ...JSON.parse(parameters), Labels: labels });
    } catch {
        return parameters;
    }
}

// Fake-data execution of the generated steps (see the back-end transformation test), stored next to the steps
// (extra "TestData" property, ignored by the back-end deserializer) so the table can be shown again on reopening.
export interface StoredTestData {
    InputColumns: { FieldId: number; Name: string }[];
    OutputColumnName: string;
    Rows: { Inputs: Record<string, string | null>; Output: string | null }[];
}

export function withTestData(parameters: string, data: StoredTestData): string {
    try {
        const { InputColumns, OutputColumnName, Rows } = data;
        return JSON.stringify({ ...JSON.parse(parameters), TestData: { InputColumns, OutputColumnName, Rows } });
    } catch {
        return parameters;
    }
}

// Returns null when there is no (usable) stored test data.
export function readTestData(parameters: string): StoredTestData | null {
    try {
        const value = pick(JSON.parse(parameters), 'TestData');
        if (value && Array.isArray(value.InputColumns) && Array.isArray(value.Rows)) {
            return { InputColumns: value.InputColumns, OutputColumnName: value.OutputColumnName ?? "", Rows: value.Rows };
        }
        return null;
    } catch {
        return null;
    }
}

// Returns null when the JSON is not a usable list of steps, [] when there is nothing yet.
export function parseSteps(parameters: string, capabilities: TransformationCapabilitiesDto[]): TransformationStep[] | null {
    if (!parameters) {
        return [];
    }
    try {
        const root = JSON.parse(parameters);
        const steps = pick(root, 'Steps');
        if (!Array.isArray(steps)) {
            return null;
        }
        const storedLabels = pick(root, 'Labels');
        return steps.map((step: any, index: number) => {
            const logicId = pick(step, 'LogicId');
            const inner = parseInnerParameters(pick(step, 'Parameters'));
            // Wiring lives in the step's own settings; tolerate it at step level too.
            const inputs = INPUT_KEYS.map((k) => pick(inner, k) ?? pick(step, k)).find((v) => v !== undefined);
            const resultId = pick(step, 'ResultId');
            return {
                Index: index + 1,
                LogicId: typeof logicId === 'number' ? logicId : null,
                Label: capabilities.find((c) => c.LogicId === logicId)?.Label ?? pick(storedLabels, String(logicId)) ?? String(logicId ?? ''),
                Details: formatDetails(inner),
                Inputs: Array.isArray(inputs) ? inputs.map(String) : (typeof inputs === 'string' && inputs ? [inputs] : []),
                ResultId: resultId ? String(resultId) : `step${index + 1}`,
            };
        });
    } catch {
        return null;
    }
}

// Transformation logic ids of the constant steps (StringConstant, Int32Constant, BigIntConstant).
const CONSTANT_LOGIC_IDS = [1001, 1002, 1003];

export interface GraphNode {
    Key: string;
    Kind: 'source' | 'constant' | 'step' | 'target';
    Title: string;
    Subtitle: string;
    Column: number;
    // Vertical position in node-slot units (fractional for a block fed by several inputs).
    Slot: number;
    // Number of input ports: one per InputFieldIds entry of the step (1 for the target, 0 for a source field or a constant).
    Ports: number;
}

export interface GraphEdge {
    From: string;
    To: string;
    // Index of the input port of `To` this edge plugs into.
    ToPort: number;
}

// Layered left-to-right layout: source fields in column 0, each step one column after its deepest
// input, and the target one column after the last step. Inputs that are not the ResultId of an
// earlier step are source fields.
export function buildGraph(
    steps: TransformationStep[],
    getFieldLabel: (id: string) => string,
    targetLabel: string
): { Nodes: GraphNode[]; Edges: GraphEdge[]; Columns: number } {
    const nodes: GraphNode[] = [];
    const edges: GraphEdge[] = [];
    const resultToKey = new Map<string, string>();
    const columnOf = new Map<string, number>();
    const sourceKeys = new Map<string, string>();

    steps.forEach((step) => {
        const key = `step:${step.Index}`;
        let column = 1;

        step.Inputs.forEach((input, port) => {
            const producer = resultToKey.get(input);
            let from: string;
            if (producer !== undefined) {
                from = producer;
            } else {
                from = sourceKeys.get(input) ?? `source:${input}`;
                if (!sourceKeys.has(input)) {
                    sourceKeys.set(input, from);
                    nodes.push({ Key: from, Kind: 'source', Title: getFieldLabel(input), Subtitle: '', Column: 0, Slot: 0, Ports: 0 });
                    columnOf.set(from, 0);
                }
            }
            edges.push({ From: from, To: key, ToPort: port });
            column = Math.max(column, (columnOf.get(from) ?? 0) + 1);
        });

        // Constants are steps (logic 1001-1003) without input: they feed the graph like a source field.
        const isConstant = step.LogicId !== null && CONSTANT_LOGIC_IDS.includes(step.LogicId) && step.Inputs.length === 0;
        if (isConstant) {
            column = 0;
        }
        nodes.push({ Key: key, Kind: isConstant ? 'constant' : 'step', Title: isConstant ? step.Details : step.Label, Subtitle: isConstant ? '' : step.Details, Column: column, Slot: 0, Ports: step.Inputs.length });
        columnOf.set(key, column);
        resultToKey.set(step.ResultId, key);
    });

    if (steps.length > 0) {
        const lastKey = `step:${steps[steps.length - 1].Index}`;
        const targetColumn = (columnOf.get(lastKey) ?? 0) + 1;
        nodes.push({ Key: 'target', Kind: 'target', Title: targetLabel, Subtitle: '', Column: targetColumn, Slot: 0, Ports: 1 });
        edges.push({ From: lastKey, To: 'target', ToPort: 0 });
    }

    // Vertical layout: walk back from the target following the input ports in order. Leaves (fields and
    // constants) take consecutive slots top to bottom, every other node sits at the mean slot of its inputs,
    // so the inputs of a block are stacked in port order and the links do not cross.
    const nodeByKey = new Map(nodes.map((n) => [n.Key, n]));
    const assigned = new Set<string>();
    let nextSlot = 0;
    const place = (key: string): void => {
        if (assigned.has(key)) {
            return;
        }
        assigned.add(key);
        const inputs = edges.filter((e) => e.To === key).sort((x, y) => x.ToPort - y.ToPort);
        inputs.forEach((e) => place(e.From));
        const node = nodeByKey.get(key)!;
        node.Slot = inputs.length === 0
            ? nextSlot++
            : inputs.reduce((sum, e) => sum + nodeByKey.get(e.From)!.Slot, 0) / inputs.length;
    };
    place('target');
    nodes.forEach((n) => place(n.Key));

    const columns = nodes.reduce((max, n) => Math.max(max, n.Column + 1), 0);
    return { Nodes: nodes, Edges: edges, Columns: columns };
}
